import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { XMarkIcon, DocumentArrowUpIcon, CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/24/outline'
import { useTranslation } from 'react-i18next'
import { attachArticlePdf, getPdfStatus } from '../../Requests/ArticleBibliographyRequests'
import { getTeamUuid } from '../../utils/cookies'
import ErrorPopup from '../PopupNotifications/ErrorPopup'

const POLL_INTERVAL_MS = 3000
const MAX_POLLS = 60

export default function AttachPdfModal({ open, setOpen, article, onDone }) {
    const { t } = useTranslation()
    const [phase, setPhase] = useState('pick') // pick | uploading | processing | done | error
    const [dragActive, setDragActive] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const fileInputRef = useRef(null)
    const pollRef = useRef(null)
    const pollCountRef = useRef(0)

    const stopPolling = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null }
    }

    useEffect(() => {
        if (!open) {
            stopPolling()
            setPhase('pick')
            setDragActive(false)
            setErrorMessage('')
            pollCountRef.current = 0
        }
    }, [open])

    useEffect(() => () => stopPolling(), [])

    const startPolling = (articleId) => {
        pollCountRef.current = 0
        pollRef.current = setInterval(async () => {
            pollCountRef.current += 1
            if (pollCountRef.current > MAX_POLLS) {
                stopPolling()
                setPhase('error')
                setErrorMessage(t('articleBibliography.pdfImport.errors.timeout'))
                return
            }
            try {
                const res = await getPdfStatus(articleId)
                const status = res.data.status
                if (status === 'processed') {
                    stopPolling()
                    setPhase('done')
                    if (onDone) onDone(res.data.article)
                } else if (status === 'failed') {
                    stopPolling()
                    setPhase('error')
                    setErrorMessage(t('articleBibliography.pdfImport.errors.processingFailed'))
                }
            } catch {
                // keep polling
            }
        }, POLL_INTERVAL_MS)
    }

    const handleFile = useCallback(async (file) => {
        if (!file) return
        if (file.type !== 'application/pdf') {
            setErrorMessage(t('articleBibliography.pdfImport.errors.pdfOnly'))
            return
        }
        if (file.size > 50 * 1024 * 1024) {
            setErrorMessage(t('articleBibliography.pdfImport.errors.tooLarge'))
            return
        }

        const formData = new FormData()
        formData.append('file', file)
        formData.append('team_uuid', getTeamUuid())

        setPhase('uploading')
        try {
            await attachArticlePdf(article.id, formData)
            setPhase('processing')
            startPolling(article.id)
        } catch (err) {
            setPhase('error')
            setErrorMessage(
                err.response?.status === 422
                    ? t('articleBibliography.pdfImport.errors.invalidFile')
                    : t('articleBibliography.pdfImport.errors.uploadFailed')
            )
        }
    }, [article, t])

    const onDragOver = (e) => { e.preventDefault(); setDragActive(true) }
    const onDragLeave = () => setDragActive(false)
    const onDrop = (e) => {
        e.preventDefault()
        setDragActive(false)
        handleFile(e.dataTransfer.files?.[0])
    }
    const onFileInputChange = (e) => {
        handleFile(e.target.files?.[0])
        e.target.value = ''
    }

    const handleClose = () => {
        if (phase === 'uploading' || phase === 'processing') return
        setOpen(false)
    }

    return (
        <>
            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={handleClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
                        leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>
                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                    {phase !== 'uploading' && phase !== 'processing' && (
                                        <div className="absolute right-0 top-0 pr-4 pt-4">
                                            <button onClick={handleClose} className="rounded-md bg-white text-gray-400 hover:text-gray-500">
                                                <XMarkIcon className="h-6 w-6" />
                                            </button>
                                        </div>
                                    )}

                                    <Dialog.Title as="h3" className="text-base font-semibold leading-6 text-gray-900 mb-1">
                                        {t('articleBibliography.attachPdf.title')}
                                    </Dialog.Title>
                                    {article && (
                                        <p className="text-sm text-gray-500 mb-4 line-clamp-2">
                                            {article.title}
                                        </p>
                                    )}

                                    {phase === 'pick' && (
                                        <div
                                            onDragOver={onDragOver}
                                            onDragLeave={onDragLeave}
                                            onDrop={onDrop}
                                            onClick={() => fileInputRef.current?.click()}
                                            className={`flex flex-col items-center justify-center border-2 border-dashed rounded-lg p-8 cursor-pointer transition-colors ${
                                                dragActive ? 'border-black bg-gray-50' : 'border-gray-300 hover:border-gray-400'
                                            }`}
                                        >
                                            <DocumentArrowUpIcon className="h-10 w-10 text-gray-400 mb-2" />
                                            <p className="text-sm font-medium text-gray-700">
                                                {t('articleBibliography.pdfImport.dropzone.prompt')}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                {t('articleBibliography.pdfImport.dropzone.hint')}
                                            </p>
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="application/pdf"
                                                className="hidden"
                                                onChange={onFileInputChange}
                                            />
                                        </div>
                                    )}

                                    {phase === 'uploading' && (
                                        <div className="flex flex-col items-center py-6 gap-3">
                                            <DocumentArrowUpIcon className="h-8 w-8 text-gray-400 animate-bounce" />
                                            <p className="text-sm text-gray-600">{t('articleBibliography.pdfImport.uploading')}</p>
                                        </div>
                                    )}

                                    {phase === 'processing' && (
                                        <div className="flex flex-col items-center py-8 gap-3">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black" />
                                            <p className="text-sm text-gray-600">{t('articleBibliography.pdfImport.processing')}</p>
                                            <p className="text-xs text-gray-500 text-center">{t('articleBibliography.pdfImport.processingHint')}</p>
                                        </div>
                                    )}

                                    {phase === 'done' && (
                                        <div className="flex flex-col items-center py-6 gap-3">
                                            <CheckCircleIcon className="h-10 w-10 text-green-500" />
                                            <p className="text-sm font-medium text-gray-700">{t('articleBibliography.attachPdf.done')}</p>
                                        </div>
                                    )}

                                    {phase === 'error' && (
                                        <div className="flex flex-col items-center py-6 gap-3">
                                            <ExclamationCircleIcon className="h-10 w-10 text-red-500" />
                                            <p className="text-sm text-gray-600 text-center">{errorMessage}</p>
                                            <button
                                                onClick={() => { setPhase('pick'); setErrorMessage('') }}
                                                className="text-sm text-black underline"
                                            >
                                                {t('articleBibliography.pdfImport.tryAgain')}
                                            </button>
                                        </div>
                                    )}

                                    <div className="mt-5 flex justify-end gap-3">
                                        {phase === 'done' && (
                                            <button
                                                onClick={() => setOpen(false)}
                                                className="inline-flex items-center rounded-md bg-black px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-800"
                                            >
                                                {t('common.close')}
                                            </button>
                                        )}
                                        {(phase === 'pick' || phase === 'error') && (
                                            <button
                                                onClick={handleClose}
                                                className="inline-flex items-center rounded-md bg-white px-4 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
                                            >
                                                {t('common.cancel')}
                                            </button>
                                        )}
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
            {errorMessage && phase !== 'error' && (
                <ErrorPopup text={errorMessage} handleClose={() => setErrorMessage('')} />
            )}
        </>
    )
}
